// Launch a browser session on one worker.
//
// The OS and browser-version choices are derived from what that worker actually
// has on disk. They used to be hardcoded (defaulting to Windows and major 120)
// while a worker might hold only Android profiles for 150 — the launch was
// accepted by the form and then failed during profile resolution, with nothing
// explaining why.

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { OsSchema } from "@veil/shared";
import { Button } from "@veil/ui/components/button";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import {
	NativeSelect,
	NativeSelectOption,
} from "@veil/ui/components/native-select";
import { Switch } from "@veil/ui/components/switch";
import { useEffect, useId, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const OS_LABELS: Record<string, string> = {
	win: "Windows",
	mac: "macOS",
	linux: "Linux",
	android: "Android",
};

/**
 * One form covering both launch modes. A discriminated union would fit the data
 * better but fights react-hook-form's flat field registry, so the mode gates the
 * checks in a refinement instead.
 */
const LaunchFormSchema = z
	.object({
		mode: z.enum(["existing", "criteria"]),
		profileId: z.string().trim(),
		os: OsSchema.or(z.literal("")),
		// Registered with valueAsNumber so the select's string value is converted
		// before validation; z.coerce here would widen the resolver's input type.
		browserMajor: z.number().int().nonnegative(),
		oneTime: z.boolean(),
	})
	.superRefine((v, ctx) => {
		if (v.mode === "existing") {
			if (v.profileId.length === 0) {
				ctx.addIssue({
					code: "custom",
					path: ["profileId"],
					message: "Enter a cloud profile ID",
				});
			}
			return;
		}
		if (v.os === "") {
			ctx.addIssue({
				code: "custom",
				path: ["os"],
				message: "Pick an operating system",
			});
		}
		if (!v.browserMajor) {
			ctx.addIssue({
				code: "custom",
				path: ["browserMajor"],
				message: "Pick a browser version",
			});
		}
	});

type LaunchFormValue = z.infer<typeof LaunchFormSchema>;

export interface LaunchInventory {
	/** major -> OSes that major has profiles for. */
	profileOsesByMajor: Record<string, string[]> | null;
	/** major -> installed profile bundle versions. */
	profilesByMajor: Record<string, string[]> | null;
}

/** Majors that have BOTH a browser build and profiles are launchable. */
function launchableMajors(inv: LaunchInventory): number[] {
	const source = inv.profileOsesByMajor ?? inv.profilesByMajor ?? {};
	return Object.keys(source)
		.map((k) => Number.parseInt(k, 10))
		.filter((n) => Number.isFinite(n))
		.sort((a, b) => b - a);
}

export function LaunchProfileCard({
	hostname,
	inventory,
	disabled,
}: {
	hostname: string;
	inventory: LaunchInventory;
	disabled?: boolean;
}) {
	const osId = useId();
	const majorId = useId();
	const profileIdField = useId();
	const oneTimeId = useId();

	const majors = useMemo(() => launchableMajors(inventory), [inventory]);
	const defaultMajor = majors[0] ?? 0;

	const form = useForm<LaunchFormValue>({
		resolver: zodResolver(LaunchFormSchema),
		mode: "onTouched",
		defaultValues: {
			mode: "existing",
			profileId: "",
			os: "",
			browserMajor: defaultMajor,
			oneTime: true,
		},
	});

	const mode = form.watch("mode");
	const browserMajor = form.watch("browserMajor");
	const oneTime = form.watch("oneTime");

	// OSes available for the selected major. Derived during render rather than
	// mirrored into state, so it cannot drift from the selection.
	const osOptions = useMemo(() => {
		const byMajor = inventory.profileOsesByMajor;
		if (!byMajor) return [];
		return byMajor[String(browserMajor)] ?? [];
	}, [inventory.profileOsesByMajor, browserMajor]);

	// Keep the OS selection valid when the major changes: the previous OS may not
	// exist in the newly selected bundle.
	useEffect(() => {
		const current = form.getValues("os");
		if (osOptions.length === 0) return;
		if (current === "" || !osOptions.includes(current)) {
			form.setValue("os", osOptions[0] as LaunchFormValue["os"], {
				shouldValidate: form.formState.isSubmitted,
			});
		}
	}, [osOptions, form]);

	const launch = useMutation({
		mutationFn: async (payload: {
			profileId?: string;
			spec?: Record<string, unknown>;
		}) => {
			const res = await fetch(
				`/api/workers/${encodeURIComponent(hostname)}/sessions`,
				{
					method: "POST",
					credentials: "include",
					headers: { "content-type": "application/json" },
					body: JSON.stringify(payload),
				},
			);
			const data = await res.json();
			if (!res.ok || !data.success) {
				throw new Error(
					data?.error?.message ?? `Launch failed (${res.status})`,
				);
			}
			return data;
		},
		onSuccess: () => toast.success("Launch requested"),
		onError: (err) =>
			toast.error(err instanceof Error ? err.message : "Launch failed"),
	});

	const submit = form.handleSubmit((values) => {
		if (values.mode === "existing") {
			launch.mutate({ profileId: values.profileId });
			return;
		}
		launch.mutate({
			spec: {
				os: values.os,
				browserVersion: values.browserMajor,
				profileType: values.oneTime ? "one-time" : "persistent",
			},
		});
	});

	const noProfiles = majors.length === 0;
	const inventoryUnknown = inventory.profileOsesByMajor === null;

	return (
		<div className="mt-4 rounded-lg border p-3">
			<div className="mb-2 flex items-center justify-between gap-2">
				<div className="text-xs uppercase text-muted-foreground">
					Launch a profile
				</div>
				<div className="flex items-center gap-1 rounded-md border p-0.5">
					{(["existing", "criteria"] as const).map((m) => (
						<Button
							key={m}
							type="button"
							variant={mode === m ? "secondary" : "ghost"}
							size="sm"
							className="h-6 px-2 text-xs"
							onClick={() => form.setValue("mode", m)}
						>
							{m === "existing" ? "Existing" : "New (criteria)"}
						</Button>
					))}
				</div>
			</div>

			<form className="flex flex-wrap items-start gap-2" onSubmit={submit}>
				{mode === "existing" ? (
					<Field
						className="min-w-56 flex-1"
						data-invalid={!!form.formState.errors.profileId}
					>
						<FieldLabel htmlFor={profileIdField} className="sr-only">
							Cloud profile ID
						</FieldLabel>
						<Input
							id={profileIdField}
							placeholder="Cloud profile ID"
							className="font-mono text-xs"
							aria-invalid={!!form.formState.errors.profileId}
							{...form.register("profileId")}
						/>
						<FieldError errors={[form.formState.errors.profileId]} />
					</Field>
				) : (
					<>
						<Field
							className="w-32"
							data-invalid={!!form.formState.errors.browserMajor}
						>
							<FieldLabel htmlFor={majorId} className="text-xs">
								Browser
							</FieldLabel>
							<NativeSelect
								id={majorId}
								size="sm"
								className="w-full"
								disabled={noProfiles}
								aria-invalid={!!form.formState.errors.browserMajor}
								{...form.register("browserMajor", { valueAsNumber: true })}
							>
								{majors.map((m) => (
									<NativeSelectOption key={m} value={m}>
										{m}
									</NativeSelectOption>
								))}
								{noProfiles ? (
									<NativeSelectOption value={0}>—</NativeSelectOption>
								) : null}
							</NativeSelect>
							<FieldError errors={[form.formState.errors.browserMajor]} />
						</Field>

						<Field className="w-36" data-invalid={!!form.formState.errors.os}>
							<FieldLabel htmlFor={osId} className="text-xs">
								Operating system
							</FieldLabel>
							<NativeSelect
								id={osId}
								size="sm"
								className="w-full"
								disabled={osOptions.length === 0}
								aria-invalid={!!form.formState.errors.os}
								{...form.register("os")}
							>
								{osOptions.map((os) => (
									<NativeSelectOption key={os} value={os}>
										{OS_LABELS[os] ?? os}
									</NativeSelectOption>
								))}
								{osOptions.length === 0 ? (
									<NativeSelectOption value="">—</NativeSelectOption>
								) : null}
							</NativeSelect>
							<FieldError errors={[form.formState.errors.os]} />
						</Field>

						<Field orientation="horizontal" className="mt-5 w-auto">
							<Switch
								id={oneTimeId}
								checked={oneTime}
								onCheckedChange={(v) => form.setValue("oneTime", v)}
							/>
							<FieldLabel htmlFor={oneTimeId} className="text-xs font-normal">
								One-time
							</FieldLabel>
						</Field>
					</>
				)}

				<Button
					type="submit"
					size="sm"
					className={mode === "criteria" ? "mt-5" : undefined}
					disabled={
						disabled || launch.isPending || (mode === "criteria" && noProfiles)
					}
				>
					{launch.isPending ? "Launching…" : "Launch"}
				</Button>
			</form>

			{mode === "criteria" && noProfiles ? (
				<FieldDescription className="mt-2">
					This worker has no profile bundles installed, so there is nothing to
					launch by criteria. Install profiles for a browser major first.
				</FieldDescription>
			) : (
				<FieldDescription className="mt-2">
					{inventoryUnknown && mode === "criteria"
						? "This worker has not reported which operating systems it has profiles for (older local-api). Options may be incomplete."
						: "The worker authorizes the launch with the cloud (license + limits) before starting the browser. One-time profiles are deleted when the browser closes."}
				</FieldDescription>
			)}
		</div>
	);
}
